'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useFormatter, useTranslations } from 'next-intl';
import { useEffect, useId, useRef } from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { SEARCH_CATEGORIES, SEARCH_TABS } from '../core/constants';
import type { SearchCategory, SearchTab } from '../core/types';
import { INDICATOR_TRANSITION } from './motion';

type SearchCounts = Record<SearchCategory, number>;

/** A tab leaving or joining the bar fades where it stands; the others slide over. */
const TAB_HIDDEN = { opacity: 0, scale: 0.9 };
const TAB_SHOWN = { opacity: 1, scale: 1 };
const TAB_EXIT = { opacity: 0, scale: 0.9, transition: { duration: 0.15 } };

/** What a tab found: its category's count, or every category's under All. */
function tabCount(tab: SearchTab, counts: SearchCounts | null): number | null {
  if (!counts) return null;
  if (tab !== 'all') return counts[tab];
  return SEARCH_CATEGORIES.reduce((sum, category) => sum + counts[category], 0);
}

interface SearchTabButtonProps {
  tab: SearchTab;
  count: number | null;
  active: boolean;
  /** One fill per tab bar, sliding from tab to tab. */
  indicatorId: string;
  onSelect: (tab: SearchTab) => void;
}

function SearchTabButton({ tab, count, active, indicatorId, onSelect }: SearchTabButtonProps) {
  const t = useTranslations('search.tabs');
  const format = useFormatter();
  const ref = useRef<HTMLButtonElement>(null);
  const handleClick = () => onSelect(tab);

  // Tab and Shift+Tab can land on a tab the bar has scrolled away from. The bar
  // scrolls itself: scrollIntoView would also move the folded panel and the page.
  useEffect(() => {
    const button = ref.current;
    const bar = button?.parentElement;
    if (!active || !button || !bar) return;
    const right = button.offsetLeft + button.offsetWidth;
    if (button.offsetLeft < bar.scrollLeft) bar.scrollLeft = button.offsetLeft;
    else if (right > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = right - bar.clientWidth;
  }, [active]);

  return (
    <motion.button
      ref={ref}
      layout="position"
      initial={TAB_HIDDEN}
      animate={TAB_SHOWN}
      exit={TAB_EXIT}
      type="button"
      role="tab"
      aria-selected={active}
      onClick={handleClick}
      className={cn(
        'relative isolate flex h-[26px] shrink-0 cursor-pointer items-center gap-1.5 rounded-[7px] px-2.5 text-[12px] font-medium leading-none outline-none transition-colors duration-150',
        active ? 'text-v2-text-primary' : 'text-v2-text-secondary hover:text-v2-text-primary',
      )}
    >
      {active ? (
        <motion.span
          layoutId={indicatorId}
          aria-hidden
          transition={INDICATOR_TRANSITION}
          className="absolute inset-0 -z-10 rounded-[7px] bg-v2-neutral-surface dark:bg-v2-surface-tertiary"
        />
      ) : null}
      {t(tab)}
      {count == null ? null : (
        <span className="text-[10.5px] tabular-nums text-v2-text-secondary">
          {format.number(count)}
        </span>
      )}
    </motion.button>
  );
}

interface SearchTabsProps {
  active: SearchTab;
  /** Per category, once a query has its first answer; none before. */
  counts: SearchCounts | null;
  onSelect: (tab: SearchTab) => void;
}

/**
 * Every tab before a query; with one, All and the categories that found
 * something, each with its count.
 */
export function SearchTabs({ active, counts, onSelect }: SearchTabsProps) {
  const t = useTranslations('search');
  const indicatorId = useId();
  const tabs = SEARCH_TABS.filter((tab) => tab === 'all' || !counts || counts[tab] > 0);
  return (
    <div
      role="tablist"
      aria-label={t('filters')}
      className="relative flex gap-0.5 overflow-x-auto px-2 pb-2 [scrollbar-width:none]"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {tabs.map((tab) => (
          <SearchTabButton
            key={tab}
            tab={tab}
            count={tabCount(tab, counts)}
            active={tab === active}
            indicatorId={indicatorId}
            onSelect={onSelect}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
